import { useCallback, useEffect, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Dialog, DialogContent, IconButton } from '@mui/material'

import { Loading } from '@common/components'

import { GENERIC_PDF_EXPORT_ERROR } from '../reportExportBundle'
import RedLineReportContent from './RedLineReportContent'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Function} props.onClose
 * @param {string} props.eventId
 */
export default function RedLineReport({ onClose, eventId }) {
  const [reportData, setReportData] = useState(null)
  const [error, setError] = useState(false)

  const {
    redlineReportDataIsLoading,
    redlineReportExportIsLoading,
    doFetchRedlineReportData,
    doExportRedlineReport,
    doShowSnackbar,
  } = useConnect(
    'selectRedlineReportDataIsLoading',
    'selectRedlineReportExportIsLoading',
    'doFetchRedlineReportData',
    'doExportRedlineReport',
    'doShowSnackbar',
  )

  const fetchReportData = useCallback(async () => {
    try {
      const response = await doFetchRedlineReportData(eventId)
      setReportData(response)
    } catch (err) {
      setError(true)
    }
  }, [eventId])

  useEffect(() => {
    if (eventId) {
      fetchReportData()
    } else {
      setError(false)
      setReportData(null)
    }
  }, [eventId])

  const downloadReportPDF = useCallback(async () => {
    try {
      await doExportRedlineReport(eventId)
    } catch (err) {
      doShowSnackbar(GENERIC_PDF_EXPORT_ERROR, 'error')
    }
  }, [eventId])

  return (
    <Dialog
      data-testid="smoke_report_dialog"
      fullScreen
      open
      onClose={onClose}
      disableScrollLock
    >
      <DialogContent sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 25,
            right: 25,
            zIndex: 1001,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Button
          variant="contained"
          sx={{
            position: 'absolute',
            top: 25,
            right: 75,
            zIndex: 1001,
          }}
          onClick={downloadReportPDF}
          disabled={error || redlineReportExportIsLoading}
        >
          Generate PDF
          {redlineReportExportIsLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                maxHeight: 20,
                paddingLeft: 2,
              }}
            >
              <Loading size={10} />
            </Box>
          )}
        </Button>
        <RedLineReportContent
          data={reportData}
          loading={redlineReportDataIsLoading}
          LoadingComponent={Loading}
          error={error}
        />
      </DialogContent>
    </Dialog>
  )
}
